import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/page-header"
import { cn } from "@/lib/utils"

export function AdminFilterPills<F extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label?: string
  value: F
  onChange: (value: F) => void
  options: readonly { value: F; label: string }[]
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {label ? (
        <span className="min-w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-[color:var(--wf-mute)]">
          {label}
        </span>
      ) : null}
      {options.map((option) => (
        <Button
          key={option.value}
          size="sm"
          variant={value === option.value ? "default" : "outline"}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  )
}

/**
 * The admin console list-page template: section header, optional filter-pill
 * row, loading skeleton, empty line, and the card list. Every /admin/* page
 * is this shape.
 */
export function AdminListPage<T extends { _id: string }, F extends string>({
  title,
  description,
  filter,
  search,
  toolbar,
  data,
  emptyCopy,
  renderRow,
}: {
  title: string
  description: string
  filter?: {
    value: F
    onChange: (value: F) => void
    options: readonly { value: F; label: string }[]
  }
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
  toolbar?: React.ReactNode
  data: T[] | undefined
  emptyCopy: string
  renderRow: (item: T) => React.ReactNode
}) {
  return (
    <div className="space-y-6">
      <PageHeader level={2} title={title} subtitle={description} />

      {search ? (
        <Input
          type="search"
          value={search.value}
          onChange={(event) => search.onChange(event.target.value)}
          placeholder={search.placeholder ?? "Search"}
          aria-label={search.placeholder ?? "Search"}
          className="max-w-md"
        />
      ) : null}

      {filter ? (
        <AdminFilterPills
          value={filter.value}
          onChange={filter.onChange}
          options={filter.options}
        />
      ) : null}

      {toolbar}

      {data === undefined && <Skeleton className="h-24 w-full" />}
      {data?.length === 0 && <EmptyState variant="plain" title={emptyCopy} />}
      <div className="space-y-3">{data?.map((item) => renderRow(item))}</div>
    </div>
  )
}

/**
 * The standard admin card row: optional media, title + badge cluster, muted
 * meta line, free-form extra content, and a trailing action block.
 */
export function AdminRow({
  title,
  badges,
  meta,
  media,
  actions,
  align = "center",
  children,
}: {
  title: React.ReactNode
  badges?: React.ReactNode
  meta?: React.ReactNode
  media?: React.ReactNode
  actions?: React.ReactNode
  align?: "center" | "start"
  children?: React.ReactNode
}) {
  return (
    <Card>
      <CardContent
        className={cn(
          "flex flex-wrap gap-4 pt-6",
          align === "center" ? "items-center" : "items-start",
        )}
      >
        {media}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-[color:var(--wf-ink)]">{title}</p>
            {badges}
          </div>
          {meta && (
            <div className="mt-1 text-sm text-[color:var(--wf-mute)]">{meta}</div>
          )}
          {children}
        </div>
        {actions}
      </CardContent>
    </Card>
  )
}
